/**
 * A2-6: global TanStack Query configuration and the single, uniform error path.
 *
 * - `queries.retry` never repeats a 4xx (client errors won't fix themselves) and
 *   retries network/5xx errors moderately (2×). A sensible `staleTime` avoids
 *   refetch storms; every successful mutation invalidates the whole cache, so
 *   lists never show pre-edit data after navigating back (Faruq 2026-09-03).
 * - `QueryCache.onError` + `MutationCache.onError` funnel every failure — including
 *   previously silent reads — through {@link handleApiError}, so exactly one toast
 *   appears per failure and no call site needs its own generic error toast.
 * - A 401 anywhere is treated as a session problem and delegated to
 *   `onUnauthorized` (see {@link ./session}); it never becomes a generic toast.
 * - Field-precise Festschreibe errors (Spez 4.4) are recognised and left for the
 *   Beleg-Editor to render at the field — they must not be swallowed by a toast.
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@werkbank/shared';
import { fieldErrorsOf } from './fieldErrors';

export interface ErrorHandlerDeps {
  /** Show one uniform error toast. */
  notifyError: (message: string) => void;
  /** Handle a 401 (discard session, route to /login, remember return target). */
  onUnauthorized: (error: ApiError) => void;
}

/**
 * Discriminator for the field-precise Festschreibe errors (Spez 4.4). The API
 * throws `BadRequestException({ message, errors: [{ field, message }], statusCode: 400 })`
 * (see apps/api/src/documents/finalize.service.ts). We key on **status 400 + an
 * `errors` array in the body**: that shape is fachliche Führung shown next to the
 * offending field (with a jump link) and must NOT surface as a generic toast.
 */
export function isFieldValidationError(error: unknown): boolean {
  // Vollanalyse S6: die Formprüfung lebt im gemeinsamen Vertrag lib/fieldErrors
  // — dieselbe Quelle, mit der die Konsumenten die Details rendern.
  return fieldErrorsOf(error) !== null;
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message || `Fehler (HTTP ${error.status}).`;
  if (error instanceof Error && error.message) return error.message;
  return 'Ein unerwarteter Fehler ist aufgetreten.';
}

/** The single error path shared by the query and mutation caches. */
export function handleApiError(error: unknown, deps: ErrorHandlerDeps): void {
  if (error instanceof ApiError && error.status === 401) {
    deps.onUnauthorized(error);
    return;
  }
  // 4.4 field errors are shown at the field by the editor, never as a toast.
  if (isFieldValidationError(error)) return;
  deps.notifyError(describeError(error));
}

export function createQueryClient(deps: ErrorHandlerDeps): QueryClient {
  const onError = (error: unknown): void => handleApiError(error, deps);
  // Der Client wird hier gebraucht, existiert aber erst nach dem Aufruf —
  // deshalb ueber eine Referenz, die der Callback erst zur Laufzeit liest.
  const ref: { current: QueryClient | null } = { current: null };
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // No point retrying a 4xx; be moderate for network/5xx errors.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({
      onError,
      // Faruq 2026-09-03: Nach JEDER erfolgreichen Aenderung gelten alle
      // gecachten Abfragen als veraltet - sichtbare laden sofort neu, die
      // uebrigen beim naechsten Aufruf. Vorher blieb eine Liste nach dem
      // Bearbeiten und Zurueckwechseln bis zu 30 s alt (staleTime), sofern die
      // Seite nicht selbst entwertete; das war auf jeder Seite einzeln zu
      // vergessen. Die Statusanzeigen laufen darin mit.
      onSuccess: () => {
        void ref.current?.invalidateQueries();
      },
    }),
  });
  ref.current = client;
  return client;
}
